import { autoJsStoreUtils } from "@droid/android";
import { Dispatcher, getProxyDispatcher, IDispatcherEventMap, IStoreWithData, Log, requestStaticData, storeUtils, toProxy, watchProperty } from "@fanfanlo";
import { ulid } from "ulid";
import { bindUserDevice, unbindUserDevice } from "../remote/user-device";
import { logout } from "../remote/user/logout";
import { IVisit, IVisitAppStartParams, tmsVisitAppStart } from "../remote/visit";
import { ITableGlobalRegisteredUser, ITablePrimeAppUser } from "../tables";
import { IIMAppUserData } from "./IMUserData";
import { IJwtData } from "src/remote";
const ns = 'user'

const fileLog = new Log(false, "[User_f]");
fileLog.pause = true;
fileLog.childrenPaused = true;
interface IUserStoreData extends IJwtData{
    user_token?:string
    refresh_token?:string
    access_token?:string
    device_info_uuid?:string
    device_id:string
    browser_id?:string
    client_user_uuid?:string
}
interface IUserSessionData{
    visit?:IVisit
    global_user_info?:ITableGlobalRegisteredUser
    prime_user_info?:ITablePrimeAppUser
}
export interface IUserDataShell {
    userInitialized:boolean
    storeData:IUserStoreData
    sessionData:IUserSessionData
    imAppUserData?:IIMAppUserData
}
function createDefaultStoreData(){
    return {
        user_token:undefined,
        refresh_token:undefined,
        access_token:undefined,
        device_info_uuid:undefined,
        device_id:'', // Initialize as empty, will be populated by checkStoreData
        browser_id:undefined,
        client_user_uuid:undefined
    }
}
function createDefaultUserData():IUserDataShell{
    return {
        userInitialized:false,
        storeData:createDefaultStoreData(),
        sessionData:{
            visit:undefined
        },
        imAppUserData:undefined
    }
}

let store:IStoreWithData<IUserStoreData>

export interface IUserEventMap extends IDispatcherEventMap{
    onUserTokenChange:(now?:string, old?:string)=>void
    onUserSignin:()=>void
    onUserSignout:()=>void
    onUserInitialized:()=>void
    onUserChanged:()=>void
}
function watchUserTokenChange(){
    watchProperty(getProxyDispatcher(user.data.storeData), 'user_token', async (now, old)=>{
        if (now === old) return;
        // console.log('user_token changed', now, old);
        await user.signin();
        user.dispatch<IUserEventMap>('onUserTokenChange', now, old)
        if(now){
            user.dispatch<IUserEventMap>('onUserSignin')
        }else{
            user.dispatch<IUserEventMap>('onUserSignout')
        }
        // 防止后续有属性继续变更，这里做成微任务使其排队到帧末执行。
        Promise.resolve().then(()=>{
            user.dispatch<IUserEventMap>('onUserChanged')
        });
    })
}

function getDeviceId(){
    const deviceId = autoJsStoreUtils.read<string>("device_id");
    if(deviceId){
        return deviceId;
    }
    if(user.data.storeData.device_id){
        return user.data.storeData.device_id;
    }
    return ulid();
}
/**
 * Checks for device_id, reading from native or generating a new one if necessary.
 * Ensures device_id is synchronized between JS and native storage.
 * @param storeData The data store to check and populate.
 */
async function checkStoreData(storeData:IUserStoreData){
    // 1. If device_id already exists in the JS store, we assume it's correct.
    if(storeData.device_id){
        // console.log(`[DEVICE_ID_SYNC] JS store already has device_id: ${storeData.device_id}`);
        return;
    }

    try {
        // 2. Try to read device_id from native storage.
        const native_id = getDeviceId()// await autoJsStoreUtils.read<string>("device_id");
        if (native_id) {
            // console.log(`[DEVICE_ID_SYNC] Read device_id from native: ${native_id}`);
            storeData.device_id = native_id;
        } else {
            // 3. If native storage is also empty, generate a new one.
            const new_id = ulid();
            // console.log(`[DEVICE_ID_SYNC] Native is empty. Generated new device_id: ${new_id}`);
            storeData.device_id = new_id;
            
            // 4. CRITICAL: Write the newly generated ID back to native storage for persistence.
            autoJsStoreUtils.write("device_id", new_id);
            // console.log(`[DEVICE_ID_SYNC] Wrote new device_id back to native.`);
        }
    } catch (e) {
        console.error('[DEVICE_ID_SYNC] Error during device_id sync with native:', e);
        // Fallback in case of native error: generate a temporary ID for the session.
        if (!storeData.device_id) {
            storeData.device_id = ulid();
            // console.warn(`[DEVICE_ID_SYNC] Using temporary session device_id: ${storeData.device_id}`);
        }
    }
}
export class User extends Dispatcher{
    static SignOutEvent = "signout"
    data = toProxy(createDefaultUserData())
    constructor(){
        super()
    }
    async init(){
        const defData = createDefaultStoreData();
        store = await storeUtils.getNamespaceStoreWithData(ns, {forceReset:false, mergeDefData:false, defData});
        this.data.storeData = store.data;

        fileLog.log('init_f', JSON.stringify(store.data))
        // Ensure device_id is properly initialized before proceeding.
        await checkStoreData(this.data.storeData);
        
        this.listen();
        
        await this.signin();
        this.data.userInitialized = true;
        this.dispatch<IUserEventMap>('onUserInitialized')
        this.dispatch<IUserEventMap>('onUserChanged')
    }
    async signin(){
        const device_id = getDeviceId();
        const params:IVisitAppStartParams = {
            device_id,
            browser_id:this.data.storeData.browser_id || ulid(),
            user_token:this.data.storeData.user_token,
            client_user_uuid:this.data.storeData.client_user_uuid,
            device_info_uuid:this.data.storeData.device_info_uuid,
        }
        requestStaticData.userToken = this.data.storeData.user_token;
        requestStaticData.deviceToken = getDeviceId;// this.data.storeData.device_id;
        const res = await tmsVisitAppStart({params, handler:{printLog:false}})
        if(res.data){
            const visit = res.data.visit;
            this.data.sessionData.visit = visit;
            if(visit.device_info_uuid){
                this.data.storeData.device_info_uuid = visit.device_info_uuid;
            }
            if(res.data.global_user_info){
                this.data.sessionData.global_user_info = res.data.global_user_info;
            }
            if(res.data.app_user_info){
                this.data.sessionData.prime_user_info = res.data.app_user_info;
            }
            if(res.data.jwt_data){
                this.setUserLogin(res.data.jwt_data)
            }
        }
        this.data.imAppUserData = {
            // homeserverUrl:res.data.visit.im_matrix_url,
            // homeserverUrl:'https://website-droid-dev.droidlego.com',
        }
    }
    private async listen(){
        watchUserTokenChange();
    }
    async signout(){
        logout();
        const old = this.data.storeData.user_token
        this.data.storeData.user_token = undefined;
        this.dispatch(User.SignOutEvent, old)
        // updateUserBindDevice(this.data.storeData.user_token, undefined);
    }
    // 这个方法需要改造，因为它没有jwt数据
    async setUserToken(token?: string) {
        const oldToken = this.data.storeData.user_token;
        this.data.storeData.user_token = token;
        updateUserBindDevice(oldToken, token)
    }

    async setUserLogin(jwtData:IJwtData){
        const fnLog = fileLog.sub(false, 'setuserLogin_f')
        // fnLog.pause = true;
        this.data.storeData.user_token = jwtData.user_token
        this.data.storeData.access_token = jwtData.access_token
        this.data.storeData.refresh_token = jwtData.refresh_token
        // fileLog.log('this.data.storeData = ', this.data.storeData)
        fnLog.log('access_token=', jwtData.access_token)
    }
    get userName(){
        const globalUserInfo = this.data.sessionData?.global_user_info;
        if(!globalUserInfo)return;
        return globalUserInfo.nickname || globalUserInfo.name || globalUserInfo.mobile || globalUserInfo.email;
    }

}

async function updateUserBindDevice(oldToken?:string, token?:string){
    console.log('token update', oldToken, token)
    if(oldToken == token)return;
    // console.log('[SYNC_FLOW] Start user-device sync process...');
        try {
            const device_id = await autoJsStoreUtils.read<string>("device_id");

            if (!device_id) {
                console.error('[SYNC_FLOW] FATAL: Could not read device_id from native. Sync aborted.');
                // 在实际应用中，可能需要弹窗提示用户
                // alert("设备初始化失败，请重启应用");
                return;
            }
            // console.log(`[SYNC_FLOW] Got device_id: ${device_id}`);

            if (token) {
                // 新 token 不为空，调用绑定接口
                // console.log('[SYNC_FLOW] Calling bind_user API...');
                await bindUserDevice({
                    params: { device_id },
                    handler: { printLog: true }
                });
                // console.log('[SYNC_FLOW] User-device bind API called successfully.');
            } else if (oldToken) {
                // token 变为空，且之前有 token，调用解绑接口
                // console.log('[SYNC_FLOW] Calling unbind_user API...');
                await unbindUserDevice({
                    params: { device_id },
                    handler: { printLog: true }
                });
                // console.log('[SYNC_FLOW] User-device unbind API called successfully.');
            }
        } catch (error) {
            console.error('[SYNC_FLOW] An error occurred during the user-device sync process:', error);
        }
}
export const user = new User();
